import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { AnimationPresenceWrapper } from '@/components/AnimationPresenceWrapper.comp';
import { FormProviderRc } from '@/components/form/FormProviderRC.comp.tsx';
import { TextFormField } from '@/components/form/TextFormField.comp.tsx';
import { TopBarFeedbackButtonDialogFormModel } from '@/components/layout/topBar/components/feedbackButton/model/TopBarFeedbackButtonDialogForm.model.ts';
import { useSubmitFeedback } from '@/components/layout/topBar/components/feedbackButton/util/useSubmitFeedback.util.ts';
import { useTopBarFeedbackButtonDialogValidation } from '@/components/layout/topBar/components/feedbackButton/util/useTopBarFeedbackButtonDialog.validation.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Props = {
  onClose: () => void;
};

export const TopBarFeedbackButtonDialog: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslationWithPrefix('topBar.feedbackButton.dialog');
  const { isSubmitting, submitFeedback } = useSubmitFeedback();
  const validation = useTopBarFeedbackButtonDialogValidation();
  const [isSent, setIsSent] = useState(false);

  const formMethods = useForm<TopBarFeedbackButtonDialogFormModel>({
    defaultValues: {
      description: '',
      subject: '',
    },
    resolver: yupResolver(validation),
  });

  const handleSubmit: SubmitHandler<TopBarFeedbackButtonDialogFormModel> = async data => {
    await submitFeedback({
      description: data.description.trim(),
      subject: data.subject.trim(),
    });
    setIsSent(true);
  };

  return (
    <Dialog open>
      <DialogTitle>{t('title')}</DialogTitle>
      {!isSent && (
        <FormProviderRc {...formMethods} validation={validation}>
          <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
            <DialogContent sx={{ py: 1 }}>
              <Stack spacing={2}>
                <Typography variant="f14Regular">{t('content1')}</Typography>
                <Typography variant="f14Regular">{t('content2')}</Typography>
                <TextFormField disabled={isSubmitting} label={t('subject')} name="subject" />
                <TextFormField
                  disabled={isSubmitting}
                  label={t('description')}
                  minRows={9}
                  multiline
                  name="description"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
                <Button onClick={onClose} type="button" variant="outlined">
                  {t('button.cancel')}
                </Button>
                <Button loading={isSubmitting} type="submit">
                  {t('button.send')}
                </Button>
              </Stack>
            </DialogActions>
          </form>
        </FormProviderRc>
      )}
      {isSent && (
        <DialogContent>
          <AnimationPresenceWrapper key="A">
            <Stack spacing={4} sx={{ alignItems: 'center', justifyContent: 'center', mt: 14 }}>
              <Typography variant="f20Medium">{t('thanks')}</Typography>
              <Button onClick={onClose}>{t('button.close')}</Button>
            </Stack>
          </AnimationPresenceWrapper>
        </DialogContent>
      )}
    </Dialog>
  );
};
